import { useFormik } from 'formik';
import { EditController } from './interfaces';
import { useEffect } from 'react';
import * as Yup from 'yup';
import { useToast } from '@chakra-ui/react';
import { useBikletaPointsService } from '../../../../../services/bikletaPoints/bikletaPointsService';
import { useStore } from '../../../../../utils/useStore';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Por favor, ingresa un título')
    .min(2, 'El título no puede contener menos de 2 caractéres')
    .max(40, 'El título no puede contener más de 40 caractéres'),
  description: Yup.string().required('Por favor, ingresa una descripción'),
  status: Yup.string().required('Elija un estado'),
  startDate: Yup.string().required('Ingrese Fecha de inicio'),
  endDate: Yup.string(),
  type: Yup.string().required('Por favor, selecciona un tipo'),
  benefitTag: Yup.string(),
  pointsCost: Yup.number()
    .typeError('Debe colocar un número')
    .min(0, 'El costo en puntos debe ser mayor o igual a 0')
    .nullable(),
  unlimitedQuantity: Yup.boolean(),
  availableQuantity: Yup.number()
    .nullable()
    .when('unlimitedQuantity', {
      is: val => val === false,
      then: Yup.number()
        .typeError('Debe colocar un número')
        .min(1, 'La cantidad debe ser mayor o igual a 1')
        .max(10000, 'La cantidad maxima es de 10.000')
        .required('Debe colocar una cantidad'),
      otherwise: null,
    }),
});

export const useEditController = (): EditController => {
  const toast = useToast();
  const { selectedBenefits } = useStore();
  const bikletaPointsService = useBikletaPointsService();
  const navigation = useNavigate();

  const handleSubmit = async values => {
    try {
      let coverUrl = values.coverUrl;
      let bannerUrl = values.bannerUrl;
      let listUrl = values.listUrl;

      // Subir imágenes si se cambiaron
      if (values.coverFile instanceof File) {
        toast({
          title: 'Subiendo imagen de portada...',
          status: 'info',
          duration: 2000,
          position: 'top-right',
        });
        const coverImage = await bikletaPointsService.postImage(values.coverFile);
        coverUrl = coverImage.url;
      }

      if (values.bannerFile instanceof File) {
        toast({
          title: 'Subiendo imagen de banner...',
          status: 'info',
          duration: 2000,
          position: 'top-right',
        });
        const bannerImage = await bikletaPointsService.postImage(values.bannerFile);
        bannerUrl = bannerImage.url;
      }

      if (values.listFile instanceof File) {
        toast({
          title: 'Subiendo imagen de lista...',
          status: 'info',
          duration: 2000,
          position: 'top-right',
        });
        const listImage = await bikletaPointsService.postImage(values.listFile);
        listUrl = listImage.url;
      }

      // Construir el objeto del beneficio
      const temporalBenefit: any = {
        _id: selectedBenefits?._id,
        title: values.title,
        description: values.description,
        status: values.status,
        type: values.type,
        startDate: values.startDate + 'T00:00:00',
        coverUrl,
        bannerUrl,
        listUrl,
      };

      // Solo agregar endDate si existe y no está vacío
      if (values.endDate && values.endDate.trim() !== '') {
        temporalBenefit.endDate = values.endDate + 'T23:59:59';
      }

      // Solo agregar benefitTag si existe
      if (values.benefitTag) {
        temporalBenefit.benefitTag = values.benefitTag;
      }

      // Solo agregar availableQuantity si no es ilimitado
      if (values.unlimitedQuantity !== true && values.availableQuantity) {
        temporalBenefit.availableQuantity = values.availableQuantity;
      }

      // 0 es un costo valido: con `if (values.pointsCost)` nunca se enviaba y el valor
      // anterior quedaba intacto. El input vacio muestra 0, por eso null se trata como 0.
      temporalBenefit.pointsCost = values.pointsCost ?? 0;

      await bikletaPointsService.updateBenefit(
        selectedBenefits?._id,
        temporalBenefit
      );
      toast({
        description: 'Beneficio editado con éxito!',
        title: 'Beneficio editado',
        position: 'top-right',
        status: 'success',
        duration: 2500,
      });
      navigation('/admin/benefits');
      return;
    } catch (error) {
      console.log(error);
      toast({
        description: 'Error al editar el beneficio',
        title: 'Ocurrió un error',
        position: 'top-right',
        status: 'error',
        duration: 2500,
      });
      return;
    }
  };

  const capitalizeFirstLetter = (str: string | undefined) => {
    if (!str || str.length === 0) {
      return '';
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  };
  const fixedStartDate = selectedBenefits?.startDate
    .toString()
    .substring(0, selectedBenefits?.startDate.toString().length - 1);

  const fixedEndDate = selectedBenefits?.endDate
    .toString()
    .substring(0, selectedBenefits?.endDate.toString().length - 1);

  const form = useFormik({
    initialValues: {
      title: selectedBenefits?.title,
      description: selectedBenefits?.description,
      type: selectedBenefits?.type,
      startDate: selectedBenefits?.startDate
        ? format(new Date(fixedStartDate), 'yyyy-MM-dd')
        : undefined,
      endDate: selectedBenefits?.endDate
        ? format(new Date(fixedEndDate), 'yyyy-MM-dd')
        : '',
      status: selectedBenefits?.status,
      coverUrl: selectedBenefits?.coverUrl,
      bannerUrl: selectedBenefits?.bannerUrl,
      listUrl: selectedBenefits?.listUrl,
      coverFile: null,
      bannerFile: null,
      listFile: null,
      benefitTag: selectedBenefits?.benefitTag,
      pointsCost: selectedBenefits?.pointsCost,
      unlimitedQuantity: !selectedBenefits?.availableQuantity,
      availableQuantity: selectedBenefits?.availableQuantity,
    },
    validationSchema: validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    console.log(form.errors);
  }, [form.errors]);

  return {
    formikInstance: form,
    capitalizeFirstLetter,
  };
};
